import type { UskladitevOtvoritve as UskladitevTip, UskladitevOtvoritveLeta } from '@koda/shared';
import { useEffect, useRef, useState, type JSX } from 'react';

import { api } from '../lib/api';

const knjizb = (n: number): string =>
  `${n.toLocaleString('sl-SI', { useGrouping: 'always' })} ${n === 1 ? 'knjižba' : 'knjižb'}`;

/** Kaj se pri letu zgodi (ali se je zgodilo), z besedami. */
function opisLeta(l: UskladitevOtvoritveLeta): string {
  const deli: string[] = [];
  if (l.vOtvoritev > 0) deli.push(`${knjizb(l.vOtvoritev)} v otvoritev (obdobje 0)`);
  if (l.izOtvoritve > 0) deli.push(`${knjizb(l.izOtvoritve)} iz otvoritve nazaj v mesec svojega datuma`);
  return deli.join(', ');
}

/**
 * Vloga otvoritve na že uvoženih letih (docs/ARHITEKTURA.md, 5.3).
 *
 * Obdobje se knjižbi določi ob uvozu, zato sprememba vloge obstoječih knjižb
 * ne premakne sama. Tu skrbnik vidi, katera leta se ne ujemajo z vlogo, in
 * jo na njih uveljavi z gumbom — številke v poročilih se premaknejo šele ob
 * kliku. Preveri se ob odprtju strani in ob vsaki spremembi vrst z vlogo
 * otvoritve (`otvoritev`).
 */
export function UskladitevOtvoritve({
  podjetje,
  otvoritev,
}: {
  podjetje: string;
  /** Vrste z vlogo otvoritve; ob spremembi se leta preverijo znova. */
  otvoritev: string;
}): JSX.Element | null {
  const [predogled, setPredogled] = useState<UskladitevTip | null>(null);
  const [izid, setIzid] = useState<UskladitevTip | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [uskladjujem, setUskladjujem] = useState(false);
  /**
   * Zaporedna številka zadnje preverbe. Velja samo odgovor zadnje: ob hitri
   * spremembi vloge bi lahko počasnejši odgovor za staro vlogo prišel za
   * novim in prepisal predogled - na primer z »vse ustreza«, čeprav po
   * novi vlogi ne. Predogled vodi do gumba, ki premakne knjižbe, zato mora
   * vedno opisovati sedanjo vlogo.
   */
  const zadnjaPreverba = useRef(0);

  const preveri = (): void => {
    const ta = ++zadnjaPreverba.current;
    setPredogled(null);
    api
      .uskladitevOtvoritve(podjetje)
      .then((p) => {
        if (ta === zadnjaPreverba.current) setPredogled(p);
      })
      .catch((e: unknown) => {
        if (ta !== zadnjaPreverba.current) return;
        setNapaka(e instanceof Error ? e.message : 'Uvoženih let ni bilo mogoče preveriti.');
      });
  };

  useEffect(() => {
    setIzid(null);
    setNapaka(null);
    preveri();
    return () => {
      // Odgovor, ki pride po zamenjavi podjetja ali vloge (ali po zaprtju strani), ne velja več.
      zadnjaPreverba.current += 1;
    };
  }, [podjetje, otvoritev]);

  const uskladi = (): void => {
    setUskladjujem(true);
    setNapaka(null);
    api
      .uskladiOtvoritev(podjetje)
      .then((r) => {
        setIzid(r);
        preveri();
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Otvoritve ni bilo mogoče uveljaviti.');
      })
      .finally(() => {
        setUskladjujem(false);
      });
  };

  const uskladjena = izid?.leta.filter((l) => l.preskoceno === null) ?? [];
  const zaUskladitev = predogled?.leta.filter((l) => l.preskoceno === null) ?? [];
  const preskocena = predogled?.leta.filter((l) => l.preskoceno !== null) ?? [];

  return (
    <div className="uskladitev-otvoritve">
      {napaka !== null && <p className="napaka">{napaka}</p>}
      {uskladjena.length > 0 && (
        <p className="uspeh-besedilo">
          Otvoritev je uveljavljena:{' '}
          {uskladjena.map((l) => `leto ${String(l.leto)} – ${opisLeta(l)}`).join('; ')}.
        </p>
      )}
      {predogled === null && napaka === null && <p className="drobno">Preverjam uvožena leta …</p>}
      {predogled !== null && predogled.leta.length === 0 && (
        <p className="drobno">Obdobja vseh uvoženih let ustrezajo vlogi otvoritve.</p>
      )}
      {predogled !== null && predogled.leta.length > 0 && (
        <div className="opozorilo">
          <p>
            Vloga otvoritve se ne ujema z že uvoženimi leti. Številke v poročilih se spremenijo
            šele, ko klikneš gumb; razporeditev knjižb po obdobjih bo enaka kot ob ponovnem uvozu
            z isto nastavitvijo otvoritve.
          </p>
          <ul>
            {zaUskladitev.map((l) => (
              <li key={l.leto}>
                <strong>{l.leto}</strong>: {opisLeta(l)}
              </li>
            ))}
            {preskocena.map((l) => (
              <li key={l.leto}>
                <strong>{l.leto}</strong>: ostane nespremenjeno. {l.preskoceno}
              </li>
            ))}
          </ul>
          {zaUskladitev.length > 0 && (
            <button type="button" className="gumb" onClick={uskladi} disabled={uskladjujem}>
              {uskladjujem ? 'Uveljavljam …' : 'Uveljavi otvoritev na uvoženih letih'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
